import {
    AbstractControl,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';

import {BusinessType} from 'web-angular-client';
import {ValidationHelper} from './validation.helper';

/**
 * Defines a set of common synchronous validators for use with form controls.
 */
export class SyncValidators
{
    static readonly PTY_INVALID_FORMAT_MESSAGE = 'Invalid PTY/LTD business registration number format, e.g. YYYY/XXXXXX/07';
    static readonly INC_INVALID_FORMAT_MESSAGE = 'Invalid incorporated company (Inc) business registration number format, e.g. YYYY/XXXXXX/21';
    static readonly CC_INVALID_FORMAT_MESSAGE = 'Invalid closed corporation (CC) business registration number format, e.g. CK YYYY/XXXXXX/23 or YYYY/XXXXXX/23';
    static readonly ANY_INVALID_FORMAT_MESSAGE = 'Invalid business registration number format, e.g. YYYY/XXXXXX/07 (PTY/LTD), YYYY/XXXXXX/21 (Inc), CK YYYY/XXXXXX/23 or YYYY/XXXXXX/23 (CC)';
    private static readonly AGENT_ID_PATTERN = /^[a-zA-Z0-9]{10,14}$/;

    /**
     * Validates whether a given business registration number is in a valid CIPC format.
     * When a business type is provided, validation is specific to that type:
     * <ul><li>PTY/LTD: YYYY/XXXXXX/07 (e.g., 2020/123456/07)</li>
     * <li>CC: CK YYYY/XXXXXX/23 or YYYY/XXXXXX/23</li>
     * <li>Inc: YYYY/XXXXXX/21</li></ul>
     *
     * @param getBusinessType Optional getter that returns the current business type string.
     *        When provided, the suffix is validated against the specific business type.
     *        When omitted, any valid CIPC format is accepted.
     * @return {ValidatorFn} A validator function that returns a validation error object
     * with `backendValidation` containing an error message if the registration number is invalid,
     * or `null` if it is valid or the control value is empty.
     */
    static validateBusinessRegistrationNumber(getBusinessType?: () => string): ValidatorFn
    {
        return (control: AbstractControl): ValidationErrors | null =>
        {
            if (!control.value)
            {
                return null;
            }

            const value = control.value.toString().replace(/\s/g, '').toUpperCase();
            const businessType = getBusinessType?.();

            let pattern: RegExp;
            let message: string;
            switch (businessType)
            {
                case BusinessType.PTY:
                    pattern = /^\d{4}\/\d{6}\/07$/;
                    message = SyncValidators.PTY_INVALID_FORMAT_MESSAGE;
                    break;
                case BusinessType.INCORPORATED_COMPANY_INC:
                    pattern = /^\d{4}\/\d{6}\/21$/;
                    message = SyncValidators.INC_INVALID_FORMAT_MESSAGE;
                    break;
                case BusinessType.CLOSED_CORPORATION:
                    pattern = /^(?:CK)?\d{4}\/\d{6}\/23$/;
                    message = SyncValidators.CC_INVALID_FORMAT_MESSAGE;
                    break;
                default:
                    pattern = /^(?:\d{4}\/\d{6}\/(?:07|21)|(?:CK)?\d{4}\/\d{6}\/23)$/;
                    message = SyncValidators.ANY_INVALID_FORMAT_MESSAGE;
                    break;
            }

            return pattern.test(value) ? null : ValidationHelper.getCustomValidatorResponse(
                'customValidation',
                message);
        };
    }

    /**
     * Validates whether a given mobile number is a valid South African mobile number.
     * Accepted formats: 0831234567, +27831234567, 27831234567 (with optional spaces or dashes).
     *
     * @return {ValidatorFn} A validator function that checks the validity of the mobile number.
     * It returns a validation error object with `invalidMobileNumber` set to true if the mobile number
     * is invalid, or `null` if it is valid or the control value is empty.
     */
    static validateMobileNumber(): ValidatorFn
    {
        return (control: AbstractControl): ValidationErrors | null =>
        {
            if (!control.value)
            {
                return null;
            }

            const cleaned = control.value.toString().replace(/[\s\-]/g, '');

            if (!/^(\+27|27|0)?[6-8]\d{8}$/.test(cleaned))
            {
                return {invalidMobileNumber: true};
            }

            return null;
        };
    }

    /**
     * Validates that passwords entered into 2 separate form controls, 'password' and 'confirmPassword' are equal.
     * Sets the following error on the 'confirmPassword' form control if the password do not match:
     *
     * ```typescript
     *  {mismatch: true}
     */
    static validatePasswordMatch()
    {
        return (form: UntypedFormGroup) =>
        {
            if (form.get('password').value !== form.get('confirmPassword').value)
            {
                form.get('confirmPassword').setErrors({'passwordMismatch': true});
                return;
            }
            form.get('confirmPassword').setErrors(null);
        };
    }

    static validatePassword(formControl: UntypedFormControl)
    {
        const LENGTH = /^.{10,}/;
        const SPECIAL_CHAR = /[$&+,:;=?@#|'<>.*()%!]/;
        const DIGIT = /\d/;
        const UPPER_CASE = /[A-Z]/;
        const LOWER_CASE = /[a-z]/;

        if (!LENGTH.test(formControl.value))
        {
            return {invalidPassword: 'The password length must be greater than or equal to 10.'};
        }

        if (!SPECIAL_CHAR.test(formControl.value))
        {
            return {invalidPassword: 'The password must contain one or more special characters ($&+,:;=?@#|\'<>.*()%!).'};
        }

        if (!DIGIT.test(formControl.value))
        {
            return {invalidPassword: 'The password must contain one or more numeric values.'};
        }

        if (!UPPER_CASE.test(formControl.value))
        {
            return {invalidPassword: 'The password must contain one or more uppercase characters.'};
        }

        if (!LOWER_CASE.test(formControl.value))
        {
            return {invalidPassword: 'The password must contain one or more lowercase characters.'};
        }
        return null;
    }

    /**
     * Validates that a number entered is a valid SA ID number. An ID number must contain 13 digits and must pass a
     * Luhn check to be considered valid.
     */
    static validateSAIdNumber(formControl: UntypedFormControl)
    {
        const idNumber: string = formControl.value;

        // Check if the ID number is 13 digits long
        if (idNumber === null || idNumber.length != 13)
        {
            return {invalidIdLength: true};
        }

        // Extract the parts of the ID number
        const birthDate = idNumber.slice(0, 6);
        const sequencePart = idNumber.slice(6, 10);
        const citizenDigit = idNumber[10];
        const racialDigit = idNumber[11];

        // Validate the date of Birth
        const parsedBirthDate = SyncValidators.convertDate(birthDate);
        if (isNaN(parsedBirthDate.getTime()) || parsedBirthDate > new Date())
        {
            return {invalidIdNumber: true};
        }

        // Validate the sequence part for gender
        const sequenceNumber = Number.parseInt(sequencePart);
        if (sequenceNumber < 0 || sequenceNumber > 9999)
        {
            return {invalidIdNumber: true};
        }

        // Validate citizenship digit
        if (citizenDigit !== '0' && citizenDigit !== '1')
        {
            return {invalidIdNumber: true};
        }

        // Validate the racial digit (historically used, now typically '8')
        if (racialDigit !== '8')
        {
            return {invalidIdNumber: true};
        }

        // Validate the checksum using the Luhn algorithm
        const idLength = idNumber.length;
        let sum = 0;
        let alternate = false;
        for (let i = idLength - 1; i >= 0; i--)
        {
            let n = Number.parseInt(idNumber.slice(i, i + 1));
            if (alternate)
            {
                n *= 2;
                if (n > 9)
                {
                    n = (n % 10) + 1;
                }
            }
            sum += n;
            alternate = !alternate;
        }
        return ((sum % 10) === 0) ? null : {invalidIdNumber: true};
    }

    /**
     * Validates whether a given agent ID is a valid alphanumeric string between 10 and 14 characters.
     * Returns null if the value is empty (field is optional at this level).
     *
     * @return {ValidatorFn} A validator function that returns a validation error if the agent ID
     * format is invalid, or null if it is valid or the control value is empty.
     */
    static validateAgentId(): ValidatorFn
    {
        return (control: AbstractControl): ValidationErrors | null =>
        {
            const trimmed = control.value?.trim();
            if (!trimmed)
            {
                return null;
            }

            if (!SyncValidators.AGENT_ID_PATTERN.test(trimmed))
            {
                return ValidationHelper.getCustomValidatorResponse(
                    'customValidation',
                    'Agent ID must be between 10 and 14 alphanumeric characters.');
            }

            return null;
        };
    }

    static validateFSPNumber()
    {
        return Validators.pattern('\\d*');
    }

    /**
     * Validates that the total value in an account is not less than the given amount.
     */
    static minimumTotalValidator(minimumTotal: number)
    {
        return (control: AbstractControl) =>
        {
            const total = control.value;
            if (total === null || parseFloat(control.value) < minimumTotal)
            {
                return ValidationHelper.getCustomValidatorResponse(
                    'customValidation',
                    `Minimum amount allowed is R ${minimumTotal.toFixed(2)}`);
            }

            return null;
        };
    }

    static maximumTotalValidator(maximumTotal: number)
    {
        return (control: AbstractControl) =>
        {
            if (parseFloat(control.value) > maximumTotal)
            {
                return ValidationHelper.getCustomValidatorResponse(
                    'customValidation',
                    `Maximum amount allowed is R ${maximumTotal.toFixed(2)}.
                     If you need a higher amount, request an increase by selecting "Get Help" in the menu.`);
            }

            return null;
        };
    }

    /**
     * Validator that checks if the multiples of the request-to-pay amount
     * exceed the configured maximum total.
     *
     * @param maximumTotal - the maximum allowed total
     * @param isVatInclusive - whether to validate against the VAT-inclusive amount
     *
     * @returns a validator function for an Angular form group.
     */
    static maximumMultiplesOfTotalValidator(maximumTotal: number, isVatInclusive: boolean)
    {
        return (form: UntypedFormGroup): Record<string, any> | null =>
        {
            const multiPremiumOptionControl = form.get('multiPremiumOption');
            const inclusiveAmountField = form.get('amountInclVat') || form.get('exactAmount');
            const exclusiveAmountField = form.get('totalAmountExclVat') || form.get('exactAmount');
            const amountControl = isVatInclusive ? inclusiveAmountField : exclusiveAmountField;

            if (!multiPremiumOptionControl || !amountControl)
            {
                return null;
            }

            const rtpAmount = +amountControl.value;
            const multiPremiumOption = +multiPremiumOptionControl.value;

            if (!multiPremiumOption || !rtpAmount)
            {
                const validationErrors = multiPremiumOptionControl.errors ?? {};

                if ('customValidation' in validationErrors)
                {
                    const {customValidation, ...rest} = validationErrors;
                    multiPremiumOptionControl.setErrors(Object.keys(rest).length ? rest : null);
                }
                return null;
            }

            const maxRtpAmount = multiPremiumOption * rtpAmount;

            if (maxRtpAmount > maximumTotal)
            {
                const existingErrors = multiPremiumOptionControl.errors ?? {};
                multiPremiumOptionControl.setErrors({
                    ...existingErrors,
                    ...ValidationHelper.getCustomValidatorResponse(
                        'customValidation',
                        `Maximum amount allowed is R ${maximumTotal.toFixed(2)}. With a multi premium option of ${multiPremiumOption}, the current maximum is R ${maxRtpAmount.toFixed(
                            2)}. If you need a higher amount, request an increase by selecting "Get Help" in the menu.`
                    )
                });
                return {customValidation: true};
            }

            const existingErrors = multiPremiumOptionControl.errors ?? {};
            if ('customValidation' in existingErrors)
            {
                const {customValidation, ...otherErrors} = existingErrors;
                multiPremiumOptionControl.setErrors(Object.keys(otherErrors).length ? otherErrors : null);
            }

            return null;
        };
    }

    /**
     * Validates that a refund amount (entered in major units, e.g. Rand) falls within the inclusive
     * [{@code minAmountCents}, {@code maxAmountCents}] range. The control value is converted to cents before
     * comparison. An empty value passes (pair with {@code Validators.required} for presence).
     *
     * @param minAmountCents minimum allowed refund, in cents
     * @param maxAmountCents maximum allowed refund, in cents (typically the amount paid)
     * @return {ValidatorFn} a validator returning a {@code customValidation} error when below/above the bounds, or
     * null when in range or empty
     */
    static refundAmountRange(minAmountCents: number, maxAmountCents: number): ValidatorFn
    {
        return (control: AbstractControl): ValidationErrors | null =>
        {
            if (control.value == null || control.value === '')
            {
                return null;
            }
            const cents = Math.round(Number(control.value) * 100);
            if (Number.isNaN(cents))
            {
                return null;
            }
            if (cents < minAmountCents)
            {
                return ValidationHelper.getCustomValidatorResponse(
                    'customValidation',
                    `Minimum refund is R ${(minAmountCents / 100).toFixed(2)}.`);
            }
            if (cents > maxAmountCents)
            {
                return ValidationHelper.getCustomValidatorResponse(
                    'customValidation',
                    `Refund cannot exceed the amount paid (R ${(maxAmountCents / 100).toFixed(2)}).`);
            }
            return null;
        };
    }

    static maximumExpiryPeriod(maximumExpiryPeriod: number)
    {
        return (control: AbstractControl) =>
        {
            if (parseFloat(control.value) > maximumExpiryPeriod)
            {
                return {maxValue: true};
            }

            return null;
        };
    }

    static validateEmailMatch()
    {
        return (form: UntypedFormGroup) =>
        {
            if (form.get('email').value !== form.get('confirmEmail').value)
            {
                form.get('confirmEmail').setErrors({'emailMismatch': true});
                return;
            }
            form.get('confirmEmail').setErrors(null);
        };
    }

    /**
     * Validates that a URL starts with http:// or https://.
     *
     * @return {ValidatorFn} A validator function that returns a custom validation error
     * with a descriptive message if the URL is invalid, or null if valid.
     */
    static validateUrl(): ValidatorFn
    {
        return (control: AbstractControl): ValidationErrors | null =>
        {
            if (!control.value)
            {
                return null;
            }

            const urlPattern = /^https?:\/\/\S+$/;
            if (!urlPattern.test(control.value))
            {
                return ValidationHelper.getCustomValidatorResponse(
                    'customValidation',
                    'Please enter a valid URL (must start with http:// or https://)');
            }

            return null;
        };
    }

    /**
     * Validates whether a given string looks like a storefront domain. Accepts an optional `http(s)://` scheme,
     * optional `www.` prefix, optional path/query/fragment/port — the server-side normalizer strips those. Requires
     * at least two DNS labels separated by a dot, each starting and ending with an alphanumeric, and a TLD of two or
     * more letters. Rejects bare names like `test4`, IP addresses, and trailing/leading/double-dot inputs.
     *
     * @return {ValidatorFn} A validator function that returns a custom validation error with a descriptive message
     * if the value is not a valid hostname, or null if valid or empty.
     */
    static validateDomain(): ValidatorFn
    {
        return (control: AbstractControl): ValidationErrors | null =>
        {
            if (!control.value)
            {
                return null;
            }

            const domainPattern =
                /^(?:https?:\/\/)?(?:www\.)?[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,}(?::\d+)?(?:[\/?#].*)?$/i;
            if (!domainPattern.test(control.value))
            {
                return ValidationHelper.getCustomValidatorResponse(
                    'customValidation',
                    'Please enter a valid storefront domain (e.g. example.com)');
            }

            return null;
        };
    }

    private static convertDate(date: string): Date
    {
        let year = parseInt(date.slice(0, 2));
        const month = parseInt(date.slice(2, 4)) - 1;
        const day = parseInt(date.slice(4, 6));
        const currentYear = new Date().getFullYear() % 100;
        year += (year <= currentYear) ? 2000 : 1900;
        const result = new Date(year, month, day);
        // Guard against JS Date silently rolling over invalid dates (e.g. month 13 → next year)
        if (result.getFullYear() !== year || result.getMonth() !== month || result.getDate() !== day)
        {
            return new Date(NaN);
        }
        return result;
    }
}